import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Sparkles, Heart, RefreshCw, Share2, Twitter, Facebook, MessageCircle, Copy, Check } from "lucide-react";
import { toast } from "sonner";

// Motivational quotes organized by mood/category
const QUOTES = {
  general: [
    { text: "Cada día es una nueva oportunidad para ser la mejor versión de ti mismo.", author: "Anónimo" },
    { text: "La felicidad no es algo hecho. Viene de tus propias acciones.", author: "Dalai Lama" },
    { text: "El único modo de hacer un gran trabajo es amar lo que haces.", author: "Steve Jobs" },
    { text: "Cree que puedes y ya estás a mitad de camino.", author: "Theodore Roosevelt" },
    { text: "La vida es 10% lo que te sucede y 90% cómo reaccionas.", author: "Charles R. Swindoll" },
    { text: "No cuentes los días, haz que los días cuenten.", author: "Muhammad Ali" },
    { text: "Tu tiempo es limitado, no lo desperdicies viviendo la vida de otro.", author: "Steve Jobs" },
    { text: "La única forma de predecir el futuro es creándolo.", author: "Peter Drucker" },
    { text: "Sé el cambio que quieres ver en el mundo.", author: "Mahatma Gandhi" },
    { text: "Lo que no te mata te hace más fuerte.", author: "Friedrich Nietzsche" },
  ],
  positive: [
    { text: "La alegría es la mejor medicina para el alma.", author: "Proverbio" },
    { text: "Brilla con luz propia, no dejes que nadie apague tu fuego interior.", author: "Anónimo" },
    { text: "Hoy es un día perfecto para ser feliz.", author: "Anónimo" },
    { text: "Tu energía positiva es contagiosa. ¡Sigue brillando!", author: "Anónimo" },
    { text: "La gratitud convierte lo que tenemos en suficiente.", author: "Melody Beattie" },
    { text: "Sonríe, es el accesorio más bonito que puedes llevar.", author: "Anónimo" },
    { text: "La felicidad no es un destino, es un modo de viajar.", author: "Margaret Lee Runbeck" },
    { text: "Eres más valiente de lo que crees, más fuerte de lo que pareces.", author: "A.A. Milne" },
  ],
  comfort: [
    { text: "Está bien no estar bien. Date permiso para sentir.", author: "Anónimo" },
    { text: "Después de la tormenta siempre viene la calma.", author: "Proverbio" },
    { text: "Eres más fuerte de lo que piensas. Esto también pasará.", author: "Anónimo" },
    { text: "No hay noche que no acabe en día.", author: "William Shakespeare" },
    { text: "Las heridas que no se ven son las que más duelen, pero también sanan.", author: "Anónimo" },
    { text: "Respira. Todo va a estar bien. Confía en el proceso.", author: "Anónimo" },
    { text: "Tu valor no disminuye por la incapacidad de alguien de ver tu valía.", author: "Anónimo" },
    { text: "Los días difíciles construyen personas fuertes.", author: "Anónimo" },
    { text: "No tienes que ser perfecto para ser increíble.", author: "Anónimo" },
    { text: "Sé amable contigo mismo. Estás haciendo lo mejor que puedes.", author: "Anónimo" },
  ],
  motivation: [
    { text: "El éxito no es definitivo, el fracaso no es fatal: lo que cuenta es el coraje de continuar.", author: "Winston Churchill" },
    { text: "Empieza donde estás. Usa lo que tienes. Haz lo que puedas.", author: "Arthur Ashe" },
    { text: "El fracaso es simplemente la oportunidad de comenzar de nuevo de forma más inteligente.", author: "Henry Ford" },
    { text: "No sueñes tu vida, vive tu sueño.", author: "Anónimo" },
    { text: "La disciplina es el puente entre metas y logros.", author: "Jim Rohn" },
    { text: "Actúa como si fuera imposible fallar.", author: "Dorothea Brande" },
    { text: "El único límite a nuestros logros de mañana son nuestras dudas de hoy.", author: "Franklin D. Roosevelt" },
    { text: "Hazlo con pasión o no lo hagas.", author: "Rosa Nouchette Carey" },
  ],
  mindfulness: [
    { text: "Vive el momento presente. Es lo único que realmente tienes.", author: "Anónimo" },
    { text: "La paz viene de adentro. No la busques afuera.", author: "Buda" },
    { text: "Inhala el futuro, exhala el pasado.", author: "Anónimo" },
    { text: "La mente es todo. En lo que piensas, te conviertes.", author: "Buda" },
    { text: "En la calma está la fuerza.", author: "Proverbio chino" },
    { text: "No puedes calmar la tormenta, pero puedes calmarte a ti mismo.", author: "Timber Hawkeye" },
    { text: "Hoy elijo la paz sobre la preocupación.", author: "Anónimo" },
    { text: "El silencio es una fuente de gran fortaleza.", author: "Lao Tzu" },
  ],
};

// Get today's quote (consistent for the whole day)
const getTodayQuote = (category = "general") => {
  const today = new Date();
  const dayOfYear = Math.floor((today - new Date(today.getFullYear(), 0, 0)) / (1000 * 60 * 60 * 24));
  
  const quotes = QUOTES[category] || QUOTES.general;
  const index = dayOfYear % quotes.length;
  
  return quotes[index];
};

// Get a random quote
const getRandomQuote = (category = "general") => {
  const quotes = QUOTES[category] || QUOTES.general;
  return quotes[Math.floor(Math.random() * quotes.length)];
};

// Determine category based on recent mood
const getCategoryFromMood = (mood) => {
  const positiveM = ["very_happy", "happy", "relaxed", "in_love", "sporty", "forgiven"];
  const needsComfort = ["sad", "very_sad", "anxious", "tired", "sick", "angry"];
  
  if (positiveM.includes(mood)) return "positive";
  if (needsComfort.includes(mood)) return "comfort";
  return "general";
};

// Share quote helper
const shareQuote = (quote, platform) => {
  const text = `"${quote.text}" — ${quote.author} ✨\n\n#MoodPunk #FraseDelDía`;
  const url = "https://moodpunk.com";
  const encodedText = encodeURIComponent(text);
  const encodedUrl = encodeURIComponent(url);

  const shareUrls = {
    twitter: `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`,
    whatsapp: `https://wa.me/?text=${encodedText}%20${encodedUrl}`,
    telegram: `https://t.me/share/url?url=${encodedUrl}&text=${encodedText}`,
  };

  if (platform === "copy") {
    navigator.clipboard.writeText(`${text}\n${url}`);
    toast.success("¡Frase copiada!");
    return;
  }

  window.open(shareUrls[platform], "_blank", "width=600,height=400");
};

// Share dropdown component
const ShareDropdown = ({ quote, isOpen, onClose }) => {
  if (!isOpen) return null;

  const platforms = [
    { id: "twitter", label: "Twitter/X", icon: Twitter, color: "hover:bg-sky-500/20 hover:text-sky-400" },
    { id: "facebook", label: "Facebook", icon: Facebook, color: "hover:bg-blue-500/20 hover:text-blue-400" },
    { id: "whatsapp", label: "WhatsApp", icon: MessageCircle, color: "hover:bg-green-500/20 hover:text-green-400" },
    { id: "telegram", label: "Telegram", icon: MessageCircle, color: "hover:bg-cyan-500/20 hover:text-cyan-400" },
    { id: "copy", label: "Copiar", icon: Copy, color: "hover:bg-white/20 hover:text-white" },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className="absolute bottom-full left-0 right-0 mb-2 p-2 bg-void-paper border border-neon-purple/30 rounded-xl shadow-lg z-10"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="grid grid-cols-5 gap-1">
        {platforms.map(({ id, label, icon: Icon, color }) => (
          <button
            key={id}
            onClick={() => {
              shareQuote(quote, id);
              if (id !== "copy") onClose();
            }}
            className={`flex flex-col items-center gap-1 p-2 rounded-lg transition-colors ${color}`}
            title={label}
          >
            <Icon className="w-5 h-5" />
            <span className="text-[10px] text-slate-400">{label}</span>
          </button>
        ))}
      </div>
    </motion.div>
  );
};

// Main Modal Component
export const DailyQuoteModal = ({ isOpen, onClose, recentMood = null }) => {
  const [quote, setQuote] = useState(null);
  const [category, setCategory] = useState("general");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showShare, setShowShare] = useState(false);

  useEffect(() => {
    if (isOpen) {
      const cat = recentMood ? getCategoryFromMood(recentMood) : "general";
      setCategory(cat);
      setQuote(getTodayQuote(cat));
      setShowShare(false);
    }
  }, [isOpen, recentMood]);

  const handleRefresh = () => {
    setIsRefreshing(true);
    setShowShare(false);
    setTimeout(() => {
      setQuote(getRandomQuote(category));
      setIsRefreshing(false);
    }, 300);
  };

  if (!isOpen || !quote) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, y: 20, opacity: 0 }}
          animate={{ scale: 1, y: 0, opacity: 1 }}
          exit={{ scale: 0.9, y: 20, opacity: 0 }}
          transition={{ type: "spring", damping: 25 }}
          className="relative max-w-lg w-full"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Decorative background */}
          <div className="absolute inset-0 bg-gradient-to-br from-neon-purple/30 via-neon-pink/20 to-neon-cyan/30 rounded-2xl blur-xl" />
          
          {/* Card */}
          <div className="relative bg-void-paper border border-neon-purple/30 rounded-2xl overflow-hidden">
            {/* Header accent */}
            <div className="h-1 bg-gradient-to-r from-neon-cyan via-neon-purple to-neon-pink" />
            
            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-2 rounded-full bg-white/5 hover:bg-white/10 text-slate-400 hover:text-white transition-colors"
              data-testid="close-quote-modal"
            >
              <X className="w-5 h-5" />
            </button>

            <div className="p-8 pt-6">
              {/* Icon */}
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring" }}
                className="w-16 h-16 mx-auto mb-6 rounded-full bg-gradient-to-br from-neon-purple/30 to-neon-pink/30 flex items-center justify-center"
              >
                <Sparkles className="w-8 h-8 text-neon-pink" />
              </motion.div>

              {/* Title */}
              <motion.h2
                initial={{ y: 10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
                className="text-center font-orbitron text-lg text-neon-cyan mb-6"
              >
                Tu Frase del Día
              </motion.h2>

              {/* Quote */}
              <motion.div
                key={quote.text}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="text-center mb-6"
              >
                <p className="text-xl md:text-2xl text-white font-light leading-relaxed mb-4">
                  &ldquo;{quote.text}&rdquo;
                </p>
                <p className="text-sm text-slate-400 font-rajdhani">
                  — {quote.author}
                </p>
              </motion.div>

              {/* Actions */}
              <motion.div
                initial={{ y: 10, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="flex flex-col sm:flex-row gap-3"
              >
                <button
                  onClick={handleRefresh}
                  disabled={isRefreshing}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-slate-300 hover:bg-white/10 hover:text-white transition-all font-rajdhani"
                  data-testid="refresh-quote-btn"
                >
                  <RefreshCw className={`w-4 h-4 ${isRefreshing ? "animate-spin" : ""}`} />
                  Otra frase
                </button>
                
                {/* Share button with dropdown */}
                <div className="relative flex-1">
                  <AnimatePresence>
                    <ShareDropdown 
                      quote={quote} 
                      isOpen={showShare} 
                      onClose={() => setShowShare(false)} 
                    />
                  </AnimatePresence>
                  <button
                    onClick={() => setShowShare(!showShare)}
                    className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl border transition-all font-rajdhani ${
                      showShare 
                        ? "bg-neon-cyan/20 border-neon-cyan/50 text-neon-cyan" 
                        : "bg-white/5 border-white/10 text-slate-300 hover:bg-white/10 hover:text-white"
                    }`}
                    data-testid="share-quote-btn"
                  >
                    <Share2 className="w-4 h-4" />
                    Compartir
                  </button>
                </div>
                
                <button
                  onClick={onClose}
                  className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gradient-to-r from-neon-purple/30 to-neon-pink/30 border border-neon-purple/50 text-white hover:from-neon-purple/40 hover:to-neon-pink/40 transition-all font-rajdhani font-semibold"
                  data-testid="start-day-btn"
                >
                  <Heart className="w-4 h-4" />
                  ¡Empezar el día!
                </button>
              </motion.div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Compact quote widget for dashboard
export const DailyQuoteWidget = ({ mood = null, className = "" }) => {
  const [quote, setQuote] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    const category = mood ? getCategoryFromMood(mood) : "general";
    setQuote(getTodayQuote(category));
  }, [mood]);

  const handleRefresh = () => {
    setIsRefreshing(true);
    const category = mood ? getCategoryFromMood(mood) : "general";
    setTimeout(() => {
      setQuote(getRandomQuote(category));
      setIsRefreshing(false);
    }, 300);
  };

  if (!quote) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`relative overflow-hidden rounded-xl bg-gradient-to-br from-neon-purple/10 via-transparent to-neon-pink/10 border border-neon-purple/20 p-4 ${className}`}
      data-testid="daily-quote-widget"
    >
      {/* Sparkle icon */}
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-neon-pink/20 flex items-center justify-center">
          <Sparkles className="w-4 h-4 text-neon-pink" />
        </div>
        
        <div className="flex-1 min-w-0">
          <p className="text-xs text-neon-cyan font-rajdhani font-semibold uppercase tracking-wide mb-1">
            Frase del día
          </p>
          <motion.p
            key={quote.text}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-sm text-white/90 leading-relaxed mb-1"
          >
            &ldquo;{quote.text}&rdquo;
          </motion.p>
          <p className="text-xs text-slate-500">— {quote.author}</p>
        </div>

        <div className="flex flex-col gap-1">
          <button
            onClick={() => shareQuote(quote, "copy")}
            className="flex-shrink-0 p-1.5 rounded-lg bg-white/5 hover:bg-neon-cyan/20 text-slate-400 hover:text-neon-cyan transition-colors"
            title="Compartir frase"
          >
            <Share2 className="w-4 h-4" />
          </button>
          <button
            onClick={handleRefresh}
            className="flex-shrink-0 p-1.5 rounded-lg bg-white/5 hover:bg-white/10 text-slate-400 hover:text-white transition-colors"
            title="Otra frase"
          >
            <RefreshCw className={`w-4 h-4 ${isRefreshing ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default DailyQuoteModal;
